import React, { useState } from "react";
import axios from "axios";
import {
  Button,
  Box,
  Typography,
  Snackbar,
  Alert,
  Grid,
  Card,
  CardContent,
  Container,
  List,
  ListItem,
  ListItemText,
  Paper,
} from "@mui/material";
import {
  FaLightbulb,
  FaCheckCircle,
  FaCode,
  FaBookOpen,
  FaRocket,
  FaMagic
} from "react-icons/fa";
import { LearnersIdeavizeSSO } from "../../../helper/base_url";

const PRIMARY_BLUE = "#1b6ef4";
const SECONDARY_BG = "#f8faff";
const DARK_TEXT = "#0A1931";
const LIGHT_TEXT = "#556987";
const CARD_SHADOW = "0 10px 40px -10px rgba(0,0,0,0.06)";

export default function IdeavizePage() {
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleSSO = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      const response = await axios.post(
        LearnersIdeavizeSSO,
        { ideavizeUrl: "https://ideavize.huebits.in/login" },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data?.IdeavizeSsoUrl) {
        window.open(
          response.data.IdeavizeSsoUrl,
          "_blank",
          "noopener,noreferrer"
        );
        setSnackbar({
          open: true,
          message: "Redirecting to Ideavize AI...",
          severity: "success",
        });
      } else {
        setSnackbar({
          open: true,
          message: "Failed to get SSO URL. Try again.",
          severity: "error",
        });
      }
    } catch (err) {
      console.error("SSO Error:", err);
      setSnackbar({
        open: true,
        message: err.response?.data?.message || "Something went wrong.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const innovationSteps = [
    {
      title: "Discover Ideas",
      description: "AI scans patents, trends, and market signals",
      icon: <FaLightbulb size={24} color={PRIMARY_BLUE} />,
      buttonText: "Start Discovery",
      action: () => console.log("Discover Ideas clicked"),
    },
    {
      title: "Validate Instantly",
      description: "Get feasibility, demand, and risk analysis",
      icon: <FaCheckCircle size={24} color={PRIMARY_BLUE} />,
      buttonText: "Validate Now",
      action: () => console.log("Validate Instantly clicked"),
    },
    {
      title: "Generate Prototype",
      description: "Auto-generate code, architecture, and build plans",
      icon: <FaCode size={24} color={PRIMARY_BLUE} />,
      buttonText: "Create Prototype",
      action: () => console.log("Generate Prototype clicked"),
    },
    {
      title: "Learn & Build",
      description: "200+ resources to master your tech",
      icon: <FaBookOpen size={24} color={PRIMARY_BLUE} />,
      buttonText: "Explore Resources",
      action: () => console.log("Learn & Build clicked"),
    },
  ];

  const llmFeatures = [
    "End-to-end innovation in one platform",
    "Faster prototyping with deeper insights",
    "Saves months of research and development",
  ];

  const perfectFor = [
    "Founders launching startups",
    "Students and researchers",
    "Enterprise innovation teams",
  ];

  const PipelineStepCard = () => (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 3,
        border: "1px solid #d0e2ff",
        mb: 6,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        bgcolor: SECONDARY_BG,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <FaRocket size={32} color={PRIMARY_BLUE} style={{ marginRight: 20 }} />
        <Box>
          <Typography variant="h6" fontWeight={700} color={DARK_TEXT}>
            Idea → Validation → Prototype → Launch
          </Typography>
          <Typography variant="body2" sx={{ color: LIGHT_TEXT }}>
            Track your entire innovation journey with auto-generated dashboards
            and real-time updates
          </Typography>
        </Box>
      </Box>
     <a
      href="https://ideavize.huebits.in/"
      target="_blank"
      rel="noopener noreferrer"
      style={{ textDecoration: 'none' }}
    >
      <Button
        variant="outlined"
        sx={{
          borderRadius: "50px",
          px: 3,
          py: 1,
          fontWeight: 600,
          borderColor: PRIMARY_BLUE,
          color: PRIMARY_BLUE,
          textTransform: "none",
          "&:hover": {
            borderColor: PRIMARY_BLUE,
            bgcolor: "#eef4ff",
          },
        }}
      >
        View Pipeline
      </Button>
    </a>
    </Paper>
  );

  return (
    <div className="ideavize-page">
      <Container maxWidth="lg" sx={{ py: 4, color: DARK_TEXT }}>
        <Box sx={{ textAlign: "left", py: 4, mb: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <FaMagic size={18} color={PRIMARY_BLUE} style={{ marginRight: 8 }} />
            <Typography
              fontWeight={600}
              sx={{ color: PRIMARY_BLUE, fontSize: "0.9rem" }}
            >
              AI-Powered Innovation Platform
            </Typography>
          </Box>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "1.5rem", md: "2rem" },
              fontWeight: 800,
              mb: 2,
              color: DARK_TEXT,
              letterSpacing: "-0.02em",
            }}
          >
            From Idea to Prototype in{" "}
            <span style={{ color: PRIMARY_BLUE }}>Minutes</span>
          </Typography>

          <Typography
            variant="body1"
            sx={{
              mb: 4,
              maxWidth: 700,
              color: LIGHT_TEXT,
              fontWeight: 400,
              lineHeight: 1.6,
            }}
          >
            Turn any idea into code, architecture, dashboards, and build plans —
            instantly. Designed for founders, students, researchers, and
            enterprises who innovate fast.
          </Typography>
        </Box>

        <Typography
          variant="h5"
          fontWeight={700}
          sx={{ mb: 3, color: DARK_TEXT }}
        >
          What You Can Do
        </Typography>
        <Grid container spacing={3} sx={{ mb: 6 }}>
          {innovationSteps.map((item, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card
                elevation={0}
                sx={{
                  height: "100%",
                  p: 3,
                  borderRadius: 3,
                  border: "1px solid #f0f0f0",
                  boxShadow: CARD_SHADOW,
                  "&:hover .icon-box": {
                    transform: "scale(1.1)",
                  },
                }}
              >
                <CardContent sx={{ p: 0, pb: "0 !important" }}>
                  <Box
                    className="icon-box"
                    sx={{
                      width: 50,
                      height: 50,
                      borderRadius: "12px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 2,
                      border: `1px solid ${PRIMARY_BLUE}40`,
                      transition: "transform 0.3s ease",
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Typography variant="h6" fontWeight={700} gutterBottom>
                    {item.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: LIGHT_TEXT, mb: 3, minHeight: 40 }}
                  >
                    {item.description}
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={handleSSO}
                    fullWidth
                    size="medium"
                    sx={{
                      borderRadius: "8px",
                      bgcolor: PRIMARY_BLUE,
                      textTransform: "none",
                      fontWeight: 600,
                    }}
                  >
                    {item.buttonText}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        <Typography
          variant="h5"
          fontWeight={700}
          sx={{ mb: 3, color: DARK_TEXT }}
        >
          Innovation Pipeline
        </Typography>
        <PipelineStepCard />

        <Grid container spacing={4} sx={{ mb: 8 }}>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 4,
                height: "100%",
                borderRadius: 3,
                border: "1px solid #f0f0f0",
              }}
            >
              <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
                Powered by Ideavize LLM
              </Typography>
              <List disablePadding>
                {llmFeatures.map((text, i) => (
                  <ListItem disableGutters key={i} sx={{ py: 0.5 }}>
                    <FaCheckCircle
                      size={14}
                      color={PRIMARY_BLUE}
                      style={{ marginRight: 10 }}
                    />
                    <ListItemText
                      primary={text}
                      primaryTypographyProps={{
                        variant: "body2",
                        color: LIGHT_TEXT,
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 4,
                height: "100%",
                borderRadius: 3,
                border: "1px solid #f0f0f0",
              }}
            >
              <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
                Perfect For
              </Typography>
              <List disablePadding>
                {perfectFor.map((text, i) => (
                  <ListItem disableGutters key={i} sx={{ py: 0.5 }}>
                    <FaCheckCircle
                      size={14}
                      color={PRIMARY_BLUE}
                      style={{ marginRight: 10 }}
                    />
                    <ListItemText
                      primary={text}
                      primaryTypographyProps={{
                        variant: "body2",
                        color: LIGHT_TEXT,
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
        </Grid>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: "100%", borderRadius: 2 }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </div>
  );
}